import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { axiosInstance } from '../../helpers/axiosInstance';
import { AppStateTypes } from '../../store/appStateTypes';
import {
  subscriptionPlansUrl,
  currentSubscriptionUrl,
  createSubscriptionUrl,
  cancelSubscriptionUrl,
  savedPaymentMethodsUrl,
  unbindPaymentMethodUrl,
  purchasePremiumUrl
} from '@pisaka/core/endpointUrls/endpointUrls';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  durationDays: number;
  features: string[];
  active: boolean;
  createdAt: string;
  tokens?: number;
  priceKzt?: number;
}

interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'expired' | 'cancelled';
  paymentId?: string;
  createdAt: string;
  plan?: SubscriptionPlan;
}

interface CurrentSubscriptionResponse {
  subscription?: UserSubscription;
  hasSubscription: boolean;
  tokensRemaining?: number;
  tokensPurchased?: number;
  tokensPlanName?: string | null;
  tokensExpireAt?: string | null;
}

interface SavedPaymentMethod {
  id: string;
  type: 'bank_card';
  saved: boolean;
  title?: string;
  card?: {
    first6: string;
    last4: string;
    expiry_year: string;
    expiry_month: string;
    card_type: string;
    issuer_country?: string;
    issuer_name?: string;
  };
}

export const useSubscription = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [savedCards, setSavedCards] = useState<SavedPaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokensRemaining, setTokensRemaining] = useState<number>(0);
  const [tokensPurchased, setTokensPurchased] = useState<number>(0);
  const [tokensPlanName, setTokensPlanName] = useState<string | null>(null);
  const [tokensExpireAt, setTokensExpireAt] = useState<string | null>(null);

  // Получаем токен авторизации из Redux
  const { token } = useSelector((state: AppStateTypes) => state.auth) || {};

  // Получить все планы подписки
  const fetchPlans = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axiosInstance(token).get(subscriptionPlansUrl);

      if (response.data.plans) {
        setPlans(response.data.plans);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Не удалось загрузить планы подписки');
      console.error('Error fetching plans:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Получить текущую подписку пользователя
  const fetchCurrentSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axiosInstance(token).get<CurrentSubscriptionResponse>(currentSubscriptionUrl);

      setCurrentSubscription(response.data.subscription ?? null);
      setHasSubscription(response.data.hasSubscription ?? false);
      setTokensRemaining(response.data.tokensRemaining ?? 0);
      setTokensPurchased(response.data.tokensPurchased ?? 0);
      setTokensPlanName(response.data.tokensPlanName ?? null);
      setTokensExpireAt(response.data.tokensExpireAt ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Не удалось загрузить подписку');
      console.error('Error fetching current subscription:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Создать подписку
  const createSubscription = useCallback(async (planId: string, paymentId?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axiosInstance(token).post(createSubscriptionUrl, {
        planId,
        paymentId,
      });

      if (response.data.subscription) {
        setCurrentSubscription(response.data.subscription);
        setHasSubscription(true);
        return response.data.subscription;
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Не удалось создать подписку');
      console.error('Error creating subscription:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Купить премиум-доступ (навсегда)
  const purchasePremium = useCallback(async (planId?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axiosInstance(token).post(purchasePremiumUrl, { planId });

      if (response.data.paymentConfirmationToken) {
        // Возвращаем токен для интеграции с ЮКассой
        return {
          paymentConfirmationToken: response.data.paymentConfirmationToken,
          status: response.data.status,
          paymentId: response.data.paymentId,
        };
      } else if (response.data.status === 'succeeded') {
        // Платеж уже прошел, обновляем подписку
        await fetchCurrentSubscription();
        return {
          status: 'succeeded',
          paymentId: response.data.paymentId,
        };
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Не удалось создать платеж для премиум-доступа');
      console.error('Error purchasing premium:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token, fetchCurrentSubscription]);

  // Отменить подписку
  const cancelSubscription = useCallback(async (subscriptionId?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axiosInstance(token).post(cancelSubscriptionUrl, {
        subscriptionId,
      });

      if (response.data.success) {
        setCurrentSubscription(null);
        setHasSubscription(false);
        return true;
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Не удалось отменить подписку');
      console.error('Error cancelling subscription:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Получить сохраненные способы оплаты
  const fetchSavedCards = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axiosInstance(token).get(savedPaymentMethodsUrl);

      if (response.data.paymentMethods) {
        setSavedCards(response.data.paymentMethods);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Не удалось загрузить сохраненные карты');
      console.error('Error fetching saved cards:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Отвязать способ оплаты
  const unbindCard = useCallback(async (cardId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      await axiosInstance(token).post(unbindPaymentMethodUrl, {
        paymentMethodId: cardId,
      });

      // Удаляем карту из локального состояния
      setSavedCards(prev => prev.filter(card => card.id !== cardId));
      
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Не удалось отвязать карту');
      console.error('Error unbinding card:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Проверить, есть ли у пользователя доступ к фиче
  const hasFeature = useCallback((feature: string): boolean => {
    if (!hasSubscription || !currentSubscription?.plan) {
      return false;
    }

    return currentSubscription.plan.features.includes(feature);
  }, [hasSubscription, currentSubscription]);

  return {
    // Состояние
    plans,
    currentSubscription,
    hasSubscription,
    savedCards,
    isLoading,
    error,
    tokensRemaining,
    tokensPurchased,
    tokensPlanName,
    tokensExpireAt,

    // Методы
    fetchPlans,
    fetchCurrentSubscription,
    createSubscription,
    purchasePremium,
    cancelSubscription,
    fetchSavedCards,
    unbindCard,
    hasFeature,

    // Утилиты
    clearError: () => setError(null),
  };
};
